import './index.less';

import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactSortable, Sortable } from 'react-sortablejs';
import { AddIcon, DeleteIcon, DragMoveIcon } from 'tdesign-icons-react';
import { Button, Collapse, Form } from 'tdesign-react';
import CollapsePanel from 'tdesign-react/es/collapse/CollapsePanel';

import { IFormItemProps } from '@/components/form/interface';

import styles from './index.module.less';
import ItemComp from './Item';
import ItemComp2 from './Item2';

const { FormItem } = Form;

const random = () => `array_${dayjs().valueOf()}`;

interface IFormArrayProps {
  fieldsConfig: IFormItemProps[];
  form: any;
  repeatConfig?: {
    [key: string]: IFormItemProps[]
  };
}

export const CustomArrayField: React.FC<
{
  value: { [key: string]: any };
  onChange: (value: { [key: string]: any }) => void;
  drag: boolean;
  buttonSize: 'small' | 'normal',
  fieldType: 'tagInput' | 'arrayInput',
  options: { label: string; value: string }[]
} & IFormArrayProps
> = (props) => {
  const {
    drag = false,
    value = {},
    options,
    onChange,
    buttonSize = 'small',
    fieldType = 'tagInput',
  } = props;

  const defaultValue: { key: string; value: any }[] = [{ key: '', value: fieldType === 'tagInput' ? [] : [''] }];
  const ref = useRef<any[]>([]);

  const [compValue, setCompValue] = useState<{ key: string; value: any }[]>([]);

  const [keys, setKeys] = useState([]?.map(() => random()));
  const [tempKeys, setTempKeys] = useState([defaultValue]?.map(() => random()));

  const [changeKeyIndex, setChangeKeyIndex] = useState<number>(-1);

  const parsedVal = useMemo(() => {
    const val: { key: string; value: any }[] = [];
    Object.keys(value).forEach((key) => {
      if (value[key] !== undefined) {
        val.push({ key, value: value[key] });
      }
    });
    return val;
  }, [value]);

  useEffect(() => {
    if (parsedVal.length > 0) {
      setCompValue([...parsedVal]);
      setArrayKeys();
      ref.current = [...parsedVal];
    }
  }, [parsedVal]);

  const transformToObj = useCallback((val: {
    key: string;
    value: any;
  }[]) => {
    const res: { [key: string]: any } = {};
    val.forEach(({ key, value }) => {
      Object.assign(res, { [key]: value });
    });
    return res;
  }, []);

  const setArrayKeys = () => {
    if (keys?.length !== parsedVal?.length) {
      const newKeys = [...parsedVal]?.map((_e, i) => {
        if (i === changeKeyIndex) {
          return `${random()}_${i}`;
        }
        return tempKeys?.[i] || `${random()}_${i}`;
      });
      setKeys(newKeys);
    }
  };

  const removeItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const newArray = [...compValue];

    newArray.splice(index, 1);
    onChange([...newArray]);
    const tempKeys = [...keys];
    tempKeys.splice(index, 1);
    setKeys(tempKeys);
    if (newArray?.length === 0) {
      setCompValue([]);
    }
  };
  const addItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const aArray = [...compValue];
    aArray.splice(index + 1, 0, ...defaultValue);
    onChange(transformToObj(aArray));

    const tempKeys = [...keys];
    tempKeys.splice(index + 1, 0, random());
    setChangeKeyIndex(index + 1);
    setTempKeys(tempKeys);
  };

  const onEnd = (evt: Sortable.SortableEvent) => {
    const oldIndex = evt.oldIndex || 0;
    const newIndex = evt.newIndex || 0;
    sortKey(oldIndex, newIndex);
    sortData(oldIndex, newIndex);
  };

  const sortData = (oldIndex: number, newIndex: number) => {
    const copyValue = [...compValue];
    const copyValue2 = [...compValue];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);
    onChange(transformToObj(copyValue));
  };

  const sortKey = (oldIndex: number, newIndex: number) => {
    const copyValue = [...keys];
    const copyValue2 = [...keys];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);
    setKeys(copyValue);
  };


  const changeArrayInfoObject = (key: number, value: { [key: string]: unknown }) => {
    const temp = [...ref.current];
    temp[key] = value;
    onChange(transformToObj([...temp]));
  };

  const handleChange = useCallback((value: any, index: number) => {
    changeArrayInfoObject(index, value as any);
  }, []);

  return (
    <div className={styles.arrayWrap}>
      <Collapse borderless style={{ padding: '8px 8px 0 8px' }} defaultExpandAll expandIconPlacement='right'>
        <CollapsePanel header={
          <Button
            className={styles.add}
            size={buttonSize === 'small' ? 'small' : 'medium'}
            onClick={event => addItem(Math.max(compValue.length - 1, 0), event)}
            style={{ padding: buttonSize === 'small' ? '0 8px' : undefined }}
            icon={<AddIcon />}
          >
            可选{fieldType === 'tagInput' ? '多选' : '描述'}字段
          </Button>}>
          <ReactSortable
            disabled={!drag}
            handle={'.draggable'}
            list={compValue.map((x: any) => ({ ...x }))}
            onEnd={onEnd}
            setList={() => { }}
          >
            {compValue.map(({ }, index) => (
              <div className="custom-form-group" style={{ alignItems: 'flex-start' }} key={`${keys?.[index]}`}>
                <div className={styles.group} style={{ flexGrow: '1' }}>
                  {fieldType === 'tagInput' && <ItemComp
                    options={
                      options.map(item => ({
                        ...item,
                        disabled: parsedVal.map(({ key }) => key).includes(item.value),
                      }))
                    }
                    value={{ ...ref.current[index] }}
                    disabled={false}
                    onChange={(value) => {
                      handleChange(value, index);
                    }} />}
                  {fieldType === 'arrayInput' && <ItemComp2 options={
                    options.map(item => ({
                      ...item,
                      disabled: parsedVal.map(({ key }) => key).includes(item.value),
                    }))
                  }
                    value={{ ...ref.current[index] }}
                    disabled={false}
                    onChange={(val) => {
                      onChange(transformToObj([...parsedVal.slice(0, index), val, ...parsedVal.slice(index + 1)]));
                    }} />}
                </div>
                <FormItem className={styles.options}>
                  <Button
                    className={styles.delete}
                    theme="default"
                    size={buttonSize === 'small' ? 'small' : 'medium'}
                    onClick={event => removeItem(index, event)}
                    icon={<DeleteIcon />}
                    style={{ padding: buttonSize === 'small' ? '0 9px' : undefined }}
                  >{buttonSize === 'normal' && '删除'}</Button>
                  {drag && <Button
                    className={classNames('draggable', styles.drag)}
                    theme="default"
                    size={buttonSize === 'small' ? 'small' : 'medium'}
                    icon={<DragMoveIcon />}
                  ></Button>}
                </FormItem>
              </div>
            ))}
          </ReactSortable>
        </CollapsePanel>
      </Collapse>
    </div>
  );
};
